'use client';

import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { generatePagination } from '../utils';
 
 

export default function Pagination({ totalPages, onPageChange, currentPage: controlledPage }: { totalPages: number; onPageChange?: (page: number) => void; currentPage?: number }) {
  // NOTE: Uncomment this code in Chapter 11
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const currentPage = controlledPage ?? (Number(searchParams.get('page')) || 1);
  const allPages = generatePagination(currentPage, totalPages);
  const createPageURL = (pageNumber: number | string) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNumber.toString());
    return `${pathname}?${params.toString()}`;
  };
  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    if (onPageChange) {
      onPageChange(nextPage);
    }
  };
  return (
    <>
      {/*  NOTE: Uncomment this code in Chapter 11 */}

      <div className="inline-flex">
        <PaginationArrow
          direction="left"
          href={createPageURL(currentPage - 1)}
          isDisabled={currentPage <= 1}
          onClick={onPageChange ? () => handlePageChange(currentPage - 1) : undefined}
        />

        <div className="flex -space-x-px">
          {allPages.map((page, index) => {
            let position: 'first' | 'last' | 'single' | 'middle' | undefined;

            if (index === 0) position = 'first';
            if (index === allPages.length - 1) position = 'last';
            if (allPages.length === 1) position = 'single';
            if (page === '...') position = 'middle';

            return (
              <PaginationNumber
                key={`${page}-${index}`}
                href={createPageURL(page)}
                page={page}
                position={position}
                isActive={currentPage === page}
                onClick={onPageChange && page !== '...' ? () => handlePageChange(page as number) : undefined}
              />
            );
          })}
        </div>

        <PaginationArrow
          direction="right"
          href={createPageURL(currentPage + 1)}
          isDisabled={currentPage >= totalPages}
          onClick={onPageChange ? () => handlePageChange(currentPage + 1) : undefined}
        />
      </div>
    </>
  );
}

function PaginationNumber({
  page,
  href,
  isActive,
  position,
  onClick,
}: {
  page: number | string;
  href: string;
  position?: 'first' | 'last' | 'middle' | 'single';
  isActive: boolean;
  onClick?: () => void;
}) {
  const className = clsx(
    'flex h-10 w-10 items-center justify-center text-sm border',
    {
      'rounded-l-md': position === 'first' || position === 'single',
      'rounded-r-md': position === 'last' || position === 'single',
      'z-10 bg-purple-600 border-blue-600 text-white': isActive,
      'hover:bg-gray-100': !isActive && position !== 'middle',
      'text-gray-300': position === 'middle',
    },
  );

  if (isActive || position === 'middle') {
    return <div className={className}>{page}</div>;
  }
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {page}
      </button>
    );
  }
  return (
    <Link to={href} className={className}>
      {page}
    </Link>
  );
}

function PaginationArrow({
  href,
  direction,
  isDisabled,
  onClick,
}: {
  href: string;
  direction: 'left' | 'right';
  isDisabled?: boolean;
  onClick?: () => void;
}) {
  const className = clsx(
    'flex h-10 w-10 items-center justify-center rounded-md border',
    {
      'pointer-events-none text-gray-300': isDisabled,
      'hover:bg-gray-100': !isDisabled,
      'mr-2 md:mr-4': direction === 'left',
      'ml-2 md:ml-4': direction === 'right',
    },
  );

  const icon =
    direction === 'left' ? (
      <ArrowLeftIcon className="w-4" />
    ) : (
      <ArrowRightIcon className="w-4" />
    );

  if (isDisabled) {
    return <div className={className}>{icon}</div>;
  }
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {icon}
      </button>
    );
  }
  return (
    <Link className={className} to={href}>
      {icon}
    </Link>
  );
}
